import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { centsToDisplay } from "@/lib/money";

export const BundleFormFields = ({ services, products, form, setForm, testIdPrefix }) => {
  const [serviceQuery, setServiceQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");

  const selectedServices = services.filter((s) => form.service_ids.includes(s.service_id));
  const selectedProducts = products.filter((p) => form.product_ids.includes(p.product_id));

  const suggestedDuration = useMemo(
    () => selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 0), 0),
    [selectedServices]
  );
  const individualTotalCents = useMemo(() => {
    const serviceTotal = selectedServices.reduce((sum, s) => sum + (s.price_cents || 0), 0);
    const productTotal = selectedProducts.reduce((sum, p) => sum + (p.price_cents || 0), 0);
    return serviceTotal + productTotal;
  }, [selectedServices, selectedProducts]);
  const bundleCents = form.price !== "" && form.price != null ? Math.round(parseFloat(form.price) * 100) : null;
  const savingsCents = bundleCents != null && !Number.isNaN(bundleCents) ? individualTotalCents - bundleCents : null;

  const toggleService = (id) => setForm((f) => {
    const has = f.service_ids.includes(id);
    const service_ids = has ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id];
    if (f.durationTouched) return { ...f, service_ids };
    const nextSuggested = services.filter((s) => service_ids.includes(s.service_id)).reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
    return { ...f, service_ids, duration_minutes: nextSuggested ? String(nextSuggested) : "" };
  });
  const toggleProduct = (id) => setForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));

  const filteredServices = services.filter((s) => s.name.toLowerCase().includes(serviceQuery.toLowerCase()));
  const filteredProducts = products.filter((p) => p.name.toLowerCase().includes(productQuery.toLowerCase()));

  const inputClass = "mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white";
  const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500";

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <label className={labelClass}>Bundle Name *</label>
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid={`${testIdPrefix}-name-input`} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Bundle SKU (optional)</label>
        <input value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} data-testid={`${testIdPrefix}-sku-input`} className={inputClass} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelClass}>Description</label>
        <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} data-testid={`${testIdPrefix}-description-input`} className={inputClass} />
      </div>

      <div className="sm:col-span-2">
        <label className={labelClass}>Included Services ({form.service_ids.length} selected)</label>
        <div className="relative mt-1">
          <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={serviceQuery} onChange={(e) => setServiceQuery(e.target.value)} placeholder="Search active services" data-testid={`${testIdPrefix}-service-search`} className="w-full rounded-xl border border-slate-300 py-2 pl-8 pr-3 text-xs dark:border-white/15 dark:bg-white/5 dark:text-white" />
        </div>
        <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-slate-200 dark:border-white/10" data-testid={`${testIdPrefix}-service-checklist`}>
          {filteredServices.length === 0 ? (
            <p className="p-3 text-xs text-slate-400">No active services found.</p>
          ) : filteredServices.map((s) => (
            <label key={s.service_id} className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0 dark:border-white/5">
              <span className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={form.service_ids.includes(s.service_id)} onChange={() => toggleService(s.service_id)} data-testid={`${testIdPrefix}-service-${s.service_id}`} />
                {s.name}
              </span>
              <span className="shrink-0 text-xs text-slate-400">{centsToDisplay(s.price_cents)}{s.duration_minutes ? ` · ${s.duration_minutes}min` : ""}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="sm:col-span-2">
        <label className={labelClass}>Included Products ({form.product_ids.length} selected)</label>
        <div className="relative mt-1">
          <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={productQuery} onChange={(e) => setProductQuery(e.target.value)} placeholder="Search active products" data-testid={`${testIdPrefix}-product-search`} className="w-full rounded-xl border border-slate-300 py-2 pl-8 pr-3 text-xs dark:border-white/15 dark:bg-white/5 dark:text-white" />
        </div>
        <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-slate-200 dark:border-white/10" data-testid={`${testIdPrefix}-product-checklist`}>
          {filteredProducts.length === 0 ? (
            <p className="p-3 text-xs text-slate-400">No active products found.</p>
          ) : filteredProducts.map((p) => (
            <label key={p.product_id} className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0 dark:border-white/5">
              <span className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={form.product_ids.includes(p.product_id)} onChange={() => toggleProduct(p.product_id)} data-testid={`${testIdPrefix}-product-${p.product_id}`} />
                {p.name}
              </span>
              <span className="shrink-0 text-xs text-slate-400">{p.price_cents != null ? centsToDisplay(p.price_cents) : "—"}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className={labelClass}>Duration (minutes)</label>
        <input
          type="number"
          min="0"
          value={form.duration_minutes}
          onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value, durationTouched: true }))}
          data-testid={`${testIdPrefix}-duration-input`}
          className={inputClass}
        />
        {suggestedDuration > 0 && String(suggestedDuration) !== String(form.duration_minutes) && (
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, duration_minutes: String(suggestedDuration), durationTouched: false }))}
            data-testid={`${testIdPrefix}-use-suggested-duration`}
            className="mt-1 text-xs font-semibold text-blue-600 hover:underline dark:text-blue-400"
          >
            Use suggested {suggestedDuration} min (from included services)
          </button>
        )}
      </div>
      <div>
        <label className={labelClass}>Bundle Price ($) *</label>
        <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid={`${testIdPrefix}-price-input`} className={inputClass} />
      </div>

      <div className="sm:col-span-2 rounded-xl bg-slate-50 p-3 text-xs dark:bg-white/5" data-testid={`${testIdPrefix}-price-summary`}>
        <p className="font-semibold text-slate-600 dark:text-slate-300">Individual item total: <span className="font-bold text-slate-950 dark:text-white">{centsToDisplay(individualTotalCents)}</span></p>
        {bundleCents != null && !Number.isNaN(bundleCents) && (
          <p className="mt-1 font-semibold text-slate-600 dark:text-slate-300">Bundle Price: <span className="font-bold text-slate-950 dark:text-white">{centsToDisplay(bundleCents)}</span></p>
        )}
        {savingsCents != null && savingsCents > 0 && (
          <p className="mt-1 font-bold text-emerald-600 dark:text-emerald-400" data-testid={`${testIdPrefix}-savings`}>Customer Savings: {centsToDisplay(savingsCents)}</p>
        )}
      </div>
    </div>
  );
};
